/**
 * Papel do usuário logado — a leitura que o front usa pra decidir o que mostrar.
 *
 * O que este hook NÃO é: autorização. Quem autoriza é a RLS e as Edge Functions,
 * no servidor. Aqui é só pra não abrir uma tela que a pessoa não pode usar —
 * hoje um aluno que digita /admin/turmas na URL **abre a tela de provisionar
 * contas**. Ela não consegue fazer nada de útil (o class-roster reconfere no
 * servidor), mas ver o painel de contas da escola já é ruim o suficiente.
 *
 * Regra do carregamento: enquanto não sabemos o papel, `carregando` é true e a
 * rota mostra o loader. Nunca renderizar o conteúdo "enquanto confere" — seria
 * exatamente o vazamento que este hook existe pra fechar.
 *
 * Falha fechada de propósito: erro de rede, sessão estranha ou zero linhas em
 * `user_roles` resultam em `aluno`. É o papel de menor poder, e é o default do
 * trigger de signup.
 *
 * Spec: .planning/PAPEIS-E-ESCOLAS.md
 */
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

/** Ordem da hierarquia. Espelha `public.nivel_do_papel` no banco. */
export const NIVEL = {
  superadmin: 100,
  admin: 100, // sinônimo legado
  diretor: 80,
  coordenador: 60,
  professor: 40,
  student: 10,
} as const;

export type Papel = keyof typeof NIVEL;

export interface PapelDoUsuario {
  /** Maior papel que a pessoa tem, em qualquer escola. */
  papel: Papel;
  nivel: number;
  /** Escolas onde ela tem algum papel. Vazio para superadmin (que é global). */
  escolas: string[];
  carregando: boolean;
}

export function nivelDe(papel: Papel | null | undefined): number {
  return papel ? (NIVEL[papel] ?? 0) : 0;
}

/** `true` se o papel alcança o mínimo pedido. */
export function alcanca(papel: Papel | null | undefined, minimo: Papel): boolean {
  return nivelDe(papel) >= NIVEL[minimo];
}

/**
 * A casa de cada papel.
 *
 * Antes, "home" era decidida pela MARCA (`branding/entrypoint.ts`:
 * globall → /console, 12brain → /aluno). Isso resolveu o problema de 03/08 — o
 * professor caía no catálogo do aluno depois de logar — mas com papéis no ar a
 * resposta passou a depender de QUEM é a pessoa, não de qual deploy ela abriu.
 * O mesmo banco serve os dois hosts: a professora que entra pelo academy é a
 * mesma que entra pelo nextgen.
 *
 * Também é o destino das setas de "voltar". Elas apontavam pra `/admin`, que
 * virou rota de superadmin — então um professor clicando em "voltar" no console
 * recebia "Esta área não é sua", no fluxo principal do usuário principal.
 *
 * Superadmin TAMBÉM mora no /console (decisão do Matheus, 07/08): o /admin é o
 * painel de conteúdo LEGADO do 12brain assíncrono — o superadmin logava pra
 * dar aula e caía num painel de outro produto, atrás de uma segunda senha.
 * O /admin continua existindo por URL, agora com portão por papel na própria
 * Edge Function (admin-videos), não por senha compartilhada.
 */
export function homeDoPapel(
  papel: Papel | null | undefined,
  /** `true` quando a marca declara que o aluno entra pela SALA, não pelo catálogo. */
  alunoEntraPelaSala = false,
  /**
   * `true` quando a marca tem menu lateral e, com ele, o `/painel`.
   *
   * ⚠️ SUPERSEDE A DECISÃO DE 07/08 acima, a pedido do José em 12/08. O motivo
   * é o mesmo que levou o superadmin pro `/console` naquele dia — cair numa tela
   * que não é a sua —, só que medido: o `/console` abre com um `select` vazio e
   * NADA mais, então quem loga pra trabalhar chega numa pergunta em vez de no
   * trabalho. O `/painel` mostra as turmas, a próxima aula de cada uma e o
   * botão que abre a sala; o `/console` continua a um clique, no menu.
   *
   * Fica em parâmetro, e não lido do módulo de marca aqui dentro, pra esta
   * função continuar pura — são 13 casos de teste em `usePapel.test.ts`.
   */
  temPainel = false,
): string {
  if (alcanca(papel, 'professor')) return temPainel ? '/painel' : '/console';
  /* No NEXTGEN o aluno não tem o catálogo do 12brain (é outro produto, em
     inglês) — desde 07/08 a casa dele é /minhas-aulas: histórico com
     reassistir, missões, quiz, e o /entrar como botão. Antes era o /entrar
     seco, só o campo do código (decisão de 03/08, superada). */
  return alunoEntraPelaSala ? '/minhas-aulas' : '/aluno';
}

export function usePapel(): PapelDoUsuario {
  const { user, isLoading: carregandoSessao } = useAuth();
  const [estado, setEstado] = useState<Omit<PapelDoUsuario, 'carregando'>>({
    papel: 'student',
    nivel: NIVEL.student,
    escolas: [],
  });
  const [carregando, setCarregando] = useState(true);

  /* ⚠️ DEPENDER DO `id`, NUNCA DO OBJETO `user`.
     `useAuth` monta um objeto novo a cada render (`user: user ? {id, email…}`),
     então `[user]` como dependência re-executa este efeito eternamente: cada
     volta chama setCarregando(true), consulta de novo e re-renderiza. O sintoma
     é a tela travada no spinner pra sempre — nem tsc, nem eslint, nem os testes
     pegam isso; só o navegador. */
  const uid = user?.id;

  useEffect(() => {
    if (carregandoSessao) return;
    if (!uid) {
      setEstado({ papel: 'student', nivel: NIVEL.student, escolas: [] });
      setCarregando(false);
      return;
    }

    let vivo = true;
    setCarregando(true);

    // A policy "Users can view their own roles" já limita ao próprio usuário;
    // o filtro explícito é redundância barata, não confiança na policy.
    supabase
      .from('user_roles')
      .select('role, school_id')
      .eq('user_id', uid)
      .then(({ data, error }) => {
        if (!vivo) return;
        if (error || !data?.length) {
          setEstado({ papel: 'student', nivel: NIVEL.student, escolas: [] });
          setCarregando(false);
          return;
        }
        const papeis = data
          .map((l) => l.role as Papel)
          .filter((p) => p in NIVEL);
        const papel = papeis.reduce<Papel>(
          (maior, p) => (NIVEL[p] > NIVEL[maior] ? p : maior),
          'student',
        );
        setEstado({
          papel,
          nivel: NIVEL[papel],
          escolas: data.map((l) => l.school_id).filter((s): s is string => Boolean(s)),
        });
        setCarregando(false);
      });

    return () => { vivo = false; };
  }, [uid, carregandoSessao]);

  return { ...estado, carregando: carregando || carregandoSessao };
}
